import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const CanaraEducationLoanEmiCal = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        "question": "What is Canara Bank’s Education Loan EMI Calculator?",
        "answer": "A free online planner tool that estimates EMIs, including moratorium-related calculations, based on your input metrics."
      },
      {
        "question": "Does it include moratorium in the EMI schedule?",
        "answer": "Yes. By adding course-duration and grace period, your EMI and repayment schedule adjust accordingly for full transparency."
      },
      {
        "question": "How reliable are the EMI estimates?",
        "answer": "Results are highly accurate using standard amortisation logic. Minor discrepancies may occur due to rounding or schedule implementation."
      },
      {
        "question": "Can I test various interest rates?",
        "answer": "Absolutely—you can simulate EMIs under different rate scenarios, helping you plan better if repayments begin at fluctuating rates."
      },
      {
        "question": "Are prepayments considered?",
        "answer": "Though the tool doesn’t auto-adjust for prepayment, you can simulate reduced EMI or tenure by lowering the principal and recalculating."
      },
      {
        "question": "Is the EMI inclusive of any fees?",
        "answer": "No. EMI covers only principal and interest. Separate charges like processing fees or insurance must be added manually to your cost estimates."
      },
      {
        "question": "How long can I plan repayment for?",
        "answer": "Canara Bank offers education loan tenures up to 15 years, including moratorium. The tool supports such extended tenure modelling."
      },
      {
        "question": "Is collateral required for all amounts?",
        "answer": "Not necessarily. Loans up to ₹7.5 lakh are usually collateral-free. For higher loans, security may be stipulated but EMI stays unaffected."
      },
      {
        "question": "What are the tax benefits available?",
        "answer": "Interest paid on education loans is eligible under Section 80E of the Income Tax Act—use EMI insights to understand potential yearly savings."
      },
      {
        "question": "Are the results exportable?",
        "answer": "EMI values and amortisation schedules can be manually saved or exported for documentation or financial advice."
      },
      {
        "question": "Can co-borrowers use this tool?",
        "answer": "Yes—the EMI output remains the same, but co-borrowers can jointly plan their contributions through a combined loan."
      },
      {
        "question": "How will floating rates affect EMI?",
        "answer": "EMI may rise or fall with rate changes. This tool helps simulate initial EMIs but ensure to check rates closer to repayment start."
      },
      {
        "question": "Is it mobile/tablet friendly?",
        "answer": "Yes—responsive design ensures seamless calculations on laptops, tablets, or smartphones, suited for on-the-go users."
      },
      {
        "question": "Can I calculate foreign course costs?",
        "answer": "Yes—convert your total overseas study cost into INR and input here to get accurate EMI planning estimates."
      },
      {
        "question": "Is this tool secure and private?",
        "answer": "Definitely. No login, no personal data tracked or stored. It’s a safe space for financial planning without risk."
      }
  ];
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>Canara Education Loan EMI Calculator - Smart EMI Planning Tool</title>
          <meta  name='description' content='Calculate Canara education loan EMI instantly with our easy-to-use calculator. Plan your education loan repayment efficiently with accurate results.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/canara-education-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">
              Canara Education Loan EMI Calculator
            </span>
          </li>
        </ol>
      </nav> */}
         <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
            Canara Education Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
        <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Plan Your Canara Bank Education Loan with TheFinMaster’s EMI Calculator
      </h2>
      <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
        In a world where higher education often translates into substantial expenses—tuition fees, overseas travel, accommodation, study materials, and more—educational loans like those from Canara Bank play a pivotal role in helping students and families pursue academic goals without financial constraints. But managing such a loan wisely requires knowing your monthly repayment in advance. Enter TheFinMaster’s Canara Bank Education Loan EMI Calculator—an intuitive tool that helps you forecast EMIs, gauge interest outgo, and visualise repayment schedules, enabling confident budgeting and smart borrowing decisions.
        Tailored to suit both undergraduate and postgraduate courses—whether in India or abroad—this tool captures moratorium, interest rates, collateral conditions, and repayment tenures. It's your ideal companion for planning a stress-free education financing journey.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        What is an Education Loan EMI?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        An Education Loan EMI is your fixed monthly repayment that covers both interest and principal components of the borrowed amount. Typically, education loans include a moratorium period—covering the study tenure plus an additional 6–12 months—during which EMIs may be deferred or only interest may be payable. Once the moratorium ends, EMI payments start based on the final eligible amount (including all accumulated interest). This breakdown ensures you repay systematically while staying informed about your outstanding obligations.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         What is the Canara Bank Education Loan EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Our Canara Bank EMI Calculator is a smart, user-friendly tool that helps forecast your monthly payments under realistic conditions. It factors in:
        <ul className="list-disc pl-6 mt-2">
          <li>Loan Amount – Total amount borrowed</li>
          <li>Interest Rate – Canara Bank’s current rate for education loans</li>
          <li>Tenure – Repayment period post-moratorium</li>
          <li>Moratorium Period – Duration of deferred EMI during studies</li>
        </ul>
        Core Features:
        <ul className="list-disc pl-6 mt-2">
          <li>Instant Outputs: Downloadable EMI, total interest, and final repayment figures</li>
          <li>Amortisation Schedule: Month-by-month breakup of EMIs</li>
          <li>Comparative Scenarios: Helps decide between shorter vs longer tenures</li>
          <li>Moratorium Mode: Accurately reflects study plus grace period</li>
          <li>Secure & Private: No sign-in or tracking—just pure utility</li>
          <li>Optimised UI: Works across devices for swift, on-the-go financial planning</li>
        </ul>
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Why Use TheFinMaster’s Canara EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <ul className="list-disc pl-6 mt-2">
          <li><strong>Precision Planning:</strong> Instant and trusted EMI calculation. No guesswork.</li>
          <li><strong>Flexible Scenarios:</strong> Adjust loan value, duration, or moratorium to see impact.</li>
          <li><strong>Visual Insights:</strong> Amortisation chart adds clarity to repayment progression.</li>
          <li><strong>Budget Alignment:</strong> Ensure EMIs align with future income after course completion.</li>
          <li><strong>Safety First:</strong> No personal data required—fully anonymous.</li>
          <li><strong>Exportable Reports:</strong> Downloadable amortisation schedule ideal for sharing or records.</li>
          <li><strong>Education-Focused:</strong> Specifically formatted for student loans like Canara Bank’s.</li>
        </ul>
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        How the Canara EMI Calculator Works
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        This tool uses the standard EMI formula:
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        EMI = [P × r × (1 + r)^N] / [(1 + r)^N – 1]
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Where:
        <ul className="list-disc pl-6 mt-2">
          <li>P = Final loan amount (including moratorium interest if capitalised)</li>
          <li>r = Monthly interest rate = Annual rate ÷ 12 ÷ 100</li>
          <li>N = Number of EMI months (excluding moratorium)</li>
        </ul>
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
         <strong>Example:</strong><br />
        Loan Amount: ₹10,00,000<br />
        Annual Rate: 10.75% → r ≈ 0.8958%<br />
        Loan Tenure: 7 years including 1-year moratorium → 72 EMIs post moratorium<br />
        EMI = ₹19,446<br />
        Total Repayment = ₹13,99,450<br />
        Total Interest = ₹3,99,450<br />
        This breakdown helps you evaluate your repayment ability and finance better.
      </p>
    </div>

    <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Sample Amortisation Table (First 12 Months After Moratorium)
      </h2>
      <div className="mt-6">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2 text-left">Month</th>
                <th className="border px-4 py-2 text-left">EMI (₹)</th>
                <th className="border px-4 py-2 text-left">Interest (₹)</th>
                <th className="border px-4 py-2 text-left">Principal (₹)</th>
                <th className="border px-4 py-2 text-left">Outstanding Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "19,446", "8,958", "10,488", "9,89,512"],
                ["2", "19,446", "8,859", "10,587", "9,78,925"],
                ["3", "19,446", "8,759", "10,687", "9,68,238"],
                ["4", "19,446", "8,657", "10,789", "9,57,449"],
                ["5", "19,446", "8,553", "10,893", "9,46,556"],
                ["6", "19,446", "8,447", "10,999", "9,35,557"],
                ["7", "19,446", "8,338", "11,108", "9,24,449"],
                ["8", "19,446", "8,227", "11,219", "9,13,230"],
                ["9", "19,446", "8,113", "11,333", "9,01,897"],
                ["10", "19,446", "7,997", "11,449", "8,90,448"],
                ["11", "19,446", "7,879", "11,567", "8,78,881"],
                ["12", "19,446", "7,759", "11,687", "8,67,194"]
              ].map(([month, emi, interest, principal, balance], index) => (
                <tr key={index}>
                  <td className="border px-4 py-2">{month}</td>
                  <td className="border px-4 py-2">{emi}</td>
                  <td className="border px-4 py-2">{interest}</td>
                  <td className="border px-4 py-2">{principal}</td>
                  <td className="border px-4 py-2">{balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-4 mt-4">
          {[
            ["1", "19,446", "8,958", "10,488", "9,89,512"],
            ["2", "19,446", "8,859", "10,587", "9,78,925"],
            ["3", "19,446", "8,759", "10,687", "9,68,238"],
            ["4", "19,446", "8,657", "10,789", "9,57,449"],
            ["5", "19,446", "8,553", "10,893", "9,46,556"],
            ["6", "19,446", "8,447", "10,999", "9,35,557"],
            ["7", "19,446", "8,338", "11,108", "9,24,449"],
            ["8", "19,446", "8,227", "11,219", "9,13,230"],
            ["9", "19,446", "8,113", "11,333", "9,01,897"],
            ["10", "19,446", "7,997", "11,449", "8,90,448"],
            ["11", "19,446", "7,879", "11,567", "8,78,881"],
            ["12", "19,446", "7,759", "11,687", "8,67,194"]
          ].map(([month, emi, interest, principal, balance], index) => (
            <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
              <div><span className="font-semibold">Month:</span> {month}</div>
              <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
              <div><span className="font-semibold">Interest (₹):</span> {interest}</div>
              <div><span className="font-semibold">Principal (₹):</span> {principal}</div>
              <div><span className="font-semibold">Outstanding Balance (₹):</span> {balance}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Factors That Influence Your EMI
      </h2>
      <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li><strong>Loan Amount</strong>
         Borrowing large amounts raises EMI and interest; smart borrowing keeps repayment manageable.</li>
        <li><strong>Interest Rate</strong>
         Even 0.5% rate difference significantly alters how much you pay back over the tenure.</li>
        <li><strong>Tenure</strong>
         Longer tenure lowers EMI but increases total interest; shorter tenure does the opposite.</li>
        <li><strong>Moratorium Duration</strong>
         Capitalisation during moratorium can increase initial EMI—balancing study relief with future repayment is essential.</li>
        <li><strong>Rate Type</strong>
         Fixed EMI ensures repayment stability; floating EMI can change with economic conditions.</li>
        <li><strong>Prepayment / Foreclosure</strong>
         Partial prepayments can reduce EMI or shorten tenure; Canara Bank allows such flexibility with minimal charges.</li>
        <li><strong>Collateral Considerations</strong>
         Loans over ₹7.5 lakh may require collateral—though EMI structure remains unaffected, the borrowing cost may increase</li>
      </ul>
    </div>
  </div>
          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
               {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CanaraEducationLoanEmiCal;
